import { useQuery } from '@tanstack/react-query';
import { CalendarDays, MapPin } from 'lucide-react';

import { calendarApi } from '@/lib/api/endpoints';
import type { CalendarEvent } from '@/lib/api/types';
import { useT } from '@/lib/i18n';

/**
 * The meetings already in today, above the tasks.
 *
 * Three hours of meetings is usually the reason a day did not go as planned,
 * and it is the one fact a task list cannot know about itself. Read-only:
 * MirOnDo never writes to anyone's calendar, so there is nothing here that can
 * move or delete an appointment.
 *
 * Absent by default — no calendar connected, none today, or Google unreachable
 * all render nothing rather than an empty box explaining itself.
 */
export function TodaySchedule() {
  const t = useT();
  const { data: events } = useQuery({
    queryKey: ['calendar', 'events', 'today'],
    queryFn: () => calendarApi.events(),
    // A meeting that appears the moment it is booked is not worth a request
    // per minute; a stale hour is.
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!events || events.length === 0) return null;

  const booked = events.reduce((total, event) => total + event.minutes, 0);

  return (
    <section className="mb-4 rounded-xl border border-border bg-surface p-3 shadow-xs">
      <header className="mb-2 flex items-center gap-2">
        <CalendarDays className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
        <h2 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
          {t('calendar.today')}
        </h2>
        {booked > 0 ? (
          <span className="text-xs tabular-nums text-content-subtle">
            {t('calendar.booked', { hours: formatHours(booked) })}
          </span>
        ) : null}
      </header>

      <ul className="space-y-1">
        {events.map((event) => (
          <li key={event.id}>
            <EventRow event={event} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function EventRow({ event }: { event: CalendarEvent }) {
  const t = useT();
  const time = event.all_day
    ? t('calendar.allDay')
    : event.starts_at
      ? formatClock(event.starts_at)
      : '';

  const row = (
    <span className="flex min-w-0 items-baseline gap-2.5 text-sm">
      <span className="w-11 shrink-0 tabular-nums text-content-subtle">{time}</span>
      <span className="min-w-0 flex-1 truncate text-content">{event.title}</span>
      {event.location ? (
        <span className="hidden min-w-0 shrink items-center gap-1 text-xs text-content-subtle sm:flex">
          <MapPin className="h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate">{event.location}</span>
        </span>
      ) : null}
    </span>
  );

  // The link opens the event in Google Calendar, which is where anything you
  // might want to *do* about a meeting lives.
  return event.url ? (
    <a
      href={event.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-md px-1.5 py-1 transition-surface hover:bg-surface-hover"
    >
      {row}
    </a>
  ) : (
    <span className="block px-1.5 py-1">{row}</span>
  );
}

function formatClock(iso: string): string {
  const date = new Date(iso);
  return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function formatHours(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}
